import { useState } from "react";
import { AlertTriangle, TrendingUp, TrendingDown, ShieldCheck, Sparkles, BookOpen, Plus, ShoppingBag } from "lucide-react";
import { NUTRIENTS } from "../pages/Nuna";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api } from "../lib/api";
import { useI18n } from "../lib/LanguageContext";
import { localizeContent } from "../lib/translations";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const UPPER_LIMIT_KEYS = ["sodium", "sugar", "sat_fat", "cholesterol"];

export default function WarningAlerts({ result, onAddFood }) {
  const { lang } = useI18n();
  const tr = (value) => localizeContent(value, lang);
  const [dialog, setDialog] = useState(null); // { nutrient, label, unit, direction }
  const [data, setData] = useState({ foods: [], featured_product: null });
  const [loading, setLoading] = useState(false);

  if (!result) return null;
  const p = result.percentages || {};

  const critical = [];
  const excess = [];
  const deficient = [];
  const ok_count = { good: 0, low: 0, high: 0 };

  NUTRIENTS.forEach(n => {
    const val = p[n.k] || 0;
    if (UPPER_LIMIT_KEYS.includes(n.k) && val > 100) { excess.push({ n, val }); ok_count.high++; }
    else if (val < 60 && n.k !== "kcal") { critical.push({ n, val, type: "low" }); ok_count.low++; }
    else if (val > 150 && !UPPER_LIMIT_KEYS.includes(n.k)) { critical.push({ n, val, type: "high" }); ok_count.high++; }
    else if (val >= 60 && val < 80) { deficient.push({ n, val }); }
    else { ok_count.good++; }
  });

  const nothing = critical.length + excess.length + deficient.length === 0;

  const openRec = async (nutrient, label, unit, direction) => {
    setDialog({ nutrient, label, unit, direction });
    setLoading(true); setData({ foods: [], featured_product: null });
    try {
      const r = await api.get(`/foods/top?nutrient=${nutrient}&direction=${direction}&limit=8`);
      setData(r.data);
    } catch { toast.error(tr("Gagal memuat rekomendasi")); }
    finally { setLoading(false); }
  };

  return (
    <div className="mb-4 space-y-3" data-testid="warning-alerts">
      <div className="grid grid-cols-3 gap-1 text-center">
        <StatChip color="teal" count={ok_count.good} label={tr("Cukup")}/>
        <StatChip color="orange" count={ok_count.low + deficient.length} label={tr("Kurang")}/>
        <StatChip color="red" count={ok_count.high} label={tr("Berlebih")}/>
      </div>

      {excess.length > 0 && (
        <AlertBox color="red" icon={<TrendingUp className="w-4 h-4"/>} title={tr("Perhatian: Kelebihan Zat Gizi")} testId="alert-excess">
          {excess.map(({ n, val }) => (
            <RecItem key={n.k} n={n} val={val} color="red" onRecommend={openRec} direction="low" testPrefix="alert-excess" lang={lang}/>
          ))}
          <p className="text-[11px] mt-2 text-red-700">{tr("Zat gizi ini melebihi batas asupan harian. Klik")} <strong>{tr("Rekomendasi Makanan")}</strong> {tr("untuk melihat alternatif rendah.")}</p>
        </AlertBox>
      )}

      {critical.filter(c => c.type === "low").length > 0 && (
        <AlertBox color="orange" icon={<TrendingDown className="w-4 h-4"/>} title={tr("Kekurangan Signifikan (< 60% AKG)")} testId="alert-low">
          {critical.filter(c => c.type === "low").map(({ n, val }) => (
            <RecItem key={n.k} n={n} val={val} color="orange" onRecommend={openRec} direction="high" testPrefix="alert-low" lang={lang}/>
          ))}
          <p className="text-[11px] mt-2 text-orange-700">{tr("Rekomendasi: tambahkan makanan sumber zat gizi ini. Klik tombol di samping tiap nutrisi.")}</p>
        </AlertBox>
      )}

      {critical.filter(c => c.type === "high").length > 0 && (
        <AlertBox color="amber" icon={<AlertTriangle className="w-4 h-4"/>} title={tr("Konsumsi Sangat Tinggi (> 150% AKG)")} testId="alert-high">
          {critical.filter(c => c.type === "high").map(({ n, val }) => (
            <RecItem key={n.k} n={n} val={val} color="amber" onRecommend={openRec} direction="low" testPrefix="alert-high" lang={lang}/>
          ))}
        </AlertBox>
      )}

      {nothing && (
        <div className="p-3 rounded-md border border-teal/20 bg-teal-soft/50 flex items-center gap-2" data-testid="alert-all-good">
          <ShieldCheck className="w-4 h-4 text-teal"/>
          <span className="text-xs text-teal font-semibold">{tr("Semua zat gizi utama berada dalam kisaran sehat.")}</span>
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o)=>!o && setDialog(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="recommendation-dialog">
          {dialog && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-teal"/>
                  {tr("Rekomendasi: ")} {dialog.direction === "high" ? tr("Tinggi") : tr("Rendah")} {tr(dialog.label)}
                </DialogTitle>
              </DialogHeader>
              {loading ? <div className="py-10 text-center text-sm text-muted-foreground">{tr("Memuat...")}</div> :
                <>
                  <p className="text-xs text-muted-foreground mb-3">{tr("Makanan dari database (TKPI, USDA, dll) — nilai per 100g. Klik")} <em>{tr("Tambah")}</em> {tr("untuk masukkan ke analisis (default 100g, bisa diubah).")}</p>
                  <div className="space-y-2">
                    {data.foods.length === 0 ? <p className="text-sm text-muted-foreground">{tr("Belum ada data.")}</p> :
                      data.foods.map(f => (
                        <div key={f.id} className="border border-teal/15 rounded-md p-3 flex items-center gap-3" data-testid={`rec-food-${f.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{f.name}</div>
                            <div className="text-xs text-muted-foreground">{f.source} · {tr(f.category)} · {f.kcal} kcal</div>
                          </div>
                          <div className="text-right text-xs">
                            <div className="font-display font-medium text-teal">{Number(f[dialog.nutrient]).toFixed(2)} {dialog.unit}</div>
                            <div className="text-[10px] text-muted-foreground">/100g</div>
                          </div>
                          <Button size="sm" className="rounded-md bg-teal hover:bg-teal/90 text-white h-8" onClick={()=>{ onAddFood?.(f); toast.success(`${f.name} ${tr("ditambahkan")}`); }} data-testid={`rec-add-${f.id}`}><Plus className="w-3.5 h-3.5 mr-1"/> {tr("Tambah")}</Button>
                        </div>
                      ))
                    }
                  </div>
                  {data.featured_product && (
                    <div className="mt-6 rounded-xl border border-teal/25 bg-gradient-to-br from-teal-soft to-white p-4 flex items-center gap-4" data-testid="upsell-product">
                      <img src={data.featured_product.image} alt={data.featured_product.name} className="w-20 h-20 object-cover rounded-md flex-shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-teal font-bold mb-1"><BookOpen className="w-3 h-3"/> {tr("Panduan Lengkap")}</div>
                        <div className="font-display text-base font-medium leading-tight">{data.featured_product.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{data.featured_product.description?.slice(0, 90)}...</div>
                        <div className="mt-2 flex items-center gap-3">
                          <div className="font-display text-sm font-medium">Rp {data.featured_product.price.toLocaleString("id-ID")}</div>
                          <Link to="/produk"><Button size="sm" className="rounded-full bg-teal hover:bg-teal/90 text-white h-8" data-testid="upsell-buy-btn"><ShoppingBag className="w-3.5 h-3.5 mr-1"/> {tr("Lihat di Toko")}</Button></Link>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              }
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecItem({ n, val, color, onRecommend, direction, testPrefix, lang }) {
  const map = { red: "text-red-600", orange: "text-orange-600", amber: "text-amber-700" };
  return (
    <li className="flex items-center justify-between gap-2 text-xs py-0.5" data-testid={`${testPrefix}-${n.k}`}>
      <span className="font-medium">{localizeContent(n.l, lang)}</span>
      <div className="flex items-center gap-2">
        <span className={`${map[color]} font-semibold`}>{val}%</span>
        <button onClick={()=>onRecommend(n.k, n.l, n.u, direction)} className="text-[10px] px-2 py-0.5 rounded-full border border-teal/30 text-teal bg-white hover:bg-teal-soft font-semibold uppercase tracking-widest" data-testid={`rec-btn-${n.k}`}>
          {localizeContent("Rekomendasi", lang)}
        </button>
      </div>
    </li>
  );
}

function StatChip({ color, count, label }) {
  const map = { teal: "bg-teal-soft text-teal border-teal/25",
                orange: "bg-orange-50 text-orange-700 border-orange-200",
                red: "bg-red-50 text-red-700 border-red-200" };
  return (
    <div className={`rounded-md border ${map[color]} py-1.5`}>
      <div className="font-display text-lg font-medium leading-none">{count}</div>
      <div className="text-[10px] uppercase tracking-widest mt-0.5">{label}</div>
    </div>
  );
}

function AlertBox({ color, icon, title, children, testId }) {
  const map = { red: "border-red-200 bg-red-50", orange: "border-orange-200 bg-orange-50", amber: "border-amber-200 bg-amber-50" };
  const titleColor = { red: "text-red-700", orange: "text-orange-700", amber: "text-amber-800" }[color];
  return (
    <div className={`rounded-md border ${map[color]} p-3`} data-testid={testId}>
      <div className={`flex items-center gap-2 ${titleColor} font-semibold text-xs uppercase tracking-widest mb-2`}>{icon} {title}</div>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}
